import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, type PrismaTableColumn, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { downloadFromUrl } from '@client/utils/common/blob';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import type { InboundEmailArchiveStatus } from '@shared/prisma/enums';
import { createFileRoute } from '@tanstack/react-router';
import { Tag, Typography } from 'antd';
import { Archive } from 'lucide-react';
import { InboundEmailArchiveFilters } from './InboundEmailArchiveFilters';

type QueryParams = RouterInputs['admin']['inboundEmailArchive']['list'];
type ArchiveRow = NonNullable<RouterOutputs['admin']['inboundEmailArchive']['list']>['rows'][number];
type ArchiveOrderBy = NonNullable<QueryParams['orderBy']>;
type ArchiveWhere = NonNullable<QueryParams['where']>;
type TableParams = Pick<QueryParams, 'limit' | 'offset' | 'orderBy' | 'where'>;
type FormatDateTime = ReturnType<typeof useFormatter>['formatDateTime'];

const STATUS_LABELS: Record<InboundEmailArchiveStatus, string> = {
    RECEIVED: 'Received',
    PROCESSED: 'Processed',
    REJECTED: 'Rejected',
    PARSE_FAILED: 'Parse failed',
    PROCESSING_FAILED: 'Processing failed',
};

const STATUS_COLORS: Record<InboundEmailArchiveStatus, string> = {
    RECEIVED: 'processing',
    PROCESSED: 'success',
    REJECTED: 'warning',
    PARSE_FAILED: 'error',
    PROCESSING_FAILED: 'error',
};

export const Route = createFileRoute('/admin-console/advanced/inbound-email-archive')({
    staticData: {
        permissions: ['admin_system:read'],
        ui: {
            icon: Archive,
            title: 'Inbound email archive',
            widthVariant: 'fullWidth',
            menuDescription: 'Original received emails retained for audit',
        },
        breadcrumb: { title: 'Inbound email archive' },
    },
    component: InboundEmailArchivePage,
});

function useArchiveColumns(
    formatDateTime: FormatDateTime,
    onDownload: (row: ArchiveRow) => void,
): readonly PrismaTableColumn<ArchiveRow, ArchiveOrderBy>[] {
    const columnHelper = createColumnHelper<ArchiveRow, ArchiveOrderBy>();

    return [
        columnHelper.accessor('receivedAt', {
            id: 'receivedAt',
            header: 'Received',
            sortable: 'receivedAt',
            cell: ({ row }) => formatDateTime(row.receivedAt),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            sortable: 'status',
            cell: ({ row }) => <Tag color={STATUS_COLORS[row.status]}>{STATUS_LABELS[row.status]}</Tag>,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('orgId', {
            id: 'orgId',
            header: 'Org',
            sortable: 'orgId',
            cell: ({ row }) =>
                row.orgId !== null ? <Typography.Text>{row.orgId.toString()}</Typography.Text> : <Tag>Unmatched</Tag>,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('fromAddress', {
            id: 'fromAddress',
            header: 'From',
            cell: ({ row }) =>
                row.fromAddress ? row.fromAddress : <Typography.Text type="secondary">—</Typography.Text>,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('toAddress', {
            id: 'toAddress',
            header: 'To',
            cell: ({ row }) => (row.toAddress ? row.toAddress : <Typography.Text type="secondary">—</Typography.Text>),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('subject', {
            id: 'subject',
            header: 'Subject',
            cell: ({ row }) => (row.subject ? row.subject : <Typography.Text type="secondary">—</Typography.Text>),
            className: 'max-w-md truncate',
        }),
        columnHelper.accessor('storedAt', {
            id: 'storedAt',
            header: 'Raw stored',
            sortable: 'storedAt',
            cell: ({ row }) => (row.storedAt ? <Tag color="success">Stored</Tag> : <Tag>Not stored</Tag>),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('downloadCount', {
            id: 'downloadCount',
            header: 'Downloads',
            sortable: 'downloadCount',
            cell: ({ row }) => row.downloadCount,
            className: 'whitespace-nowrap',
        }),
        columnHelper.display({
            id: 'actions',
            header: '',
            cell: ({ row }) =>
                row.storedAt ? (
                    <Button.Download
                        size="small"
                        tooltip="Download original email (.eml)"
                        onClick={() => onDownload(row)}
                    />
                ) : (
                    <Typography.Text type="secondary">—</Typography.Text>
                ),
        }),
    ] as const;
}

function InboundEmailArchivePage() {
    const { trpc, trpcClient } = useTrpc();
    const { formatDateTime } = useFormatter();

    // `where` lives in the same persisted path state as pagination/sort so a shared/restored URL
    // re-applies the filter on the initial query — the filter component suppresses its initial
    // onChange on restoration, so a separate parent state would otherwise stay undefined.
    const [queryVariables, setQueryVariables] = usePathState<TableParams>('queryVariables', {
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        orderBy: { receivedAt: 'desc' },
    });

    // Reset to the first page whenever the filter set changes, so a narrowed result set never
    // strands the user on an out-of-range page.
    const handleFilterChange = (where: ArchiveWhere) => {
        setQueryVariables((prev) => ({ ...prev, where, offset: 0 }));
    };

    const { data, isPending, error, refetch } = trpc.admin.inboundEmailArchive.list.useQuery(queryVariables);

    const handleDownload = async (row: ArchiveRow) => {
        try {
            const { url } = await trpcClient.admin.inboundEmailArchive.getRawEmailDownloadUrl.query({
                archiveId: row.id,
            });
            downloadFromUrl(url);
        } catch (err) {
            errorMessage.show(err);
        }
    };

    const columns = useArchiveColumns(formatDateTime, handleDownload);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        columns,
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: { position: 'both', showSizeChanger: true },
        refreshable: { enabled: true, onRefresh: () => refetch() },
    });

    return (
        <PageController route={Route}>
            <TableToolbar filters={<InboundEmailArchiveFilters onChange={handleFilterChange} />} variant="enhanced" />
            {error ? (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    title="Couldn't load inbound email archive"
                />
            ) : (
                <PrismaTable {...tableProps} scroll={{ x: 'max-content' }} />
            )}
        </PageController>
    );
}
